import { formatMetricForCell } from "@/grid/core/format-metric";
import { cn } from "@/lib/cn";
import { Fragment, useMemo } from "react";

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  color: string;
  sharePct: number;
}

interface PieChartProps {
  slices: DonutSlice[];
  className?: string;
  /** "tv" = pie + large side legend (no leader labels) for the Company Sales TV display. */
  size?: "default" | "tv";
}

function polarToCartesian(cx: number, cy: number, radius: number, angleDeg: number) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

function describePieSlice(
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
): string {
  if (endAngle - startAngle >= 360) {
    return `M ${cx - radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx + radius} ${cy} A ${radius} ${radius} 0 1 1 ${cx - radius} ${cy} Z`;
  }

  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;

  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
}

function truncateLabel(label: string, max = 14): string {
  return label.length > max ? `${label.slice(0, max - 1)}…` : label;
}

export function DonutChart({ slices, className, size: variant = "default" }: PieChartProps) {
  const size = 420;
  const total = useMemo(() => slices.reduce((sum, slice) => sum + slice.value, 0), [slices]);

  const arcs = useMemo(() => {
    if (total <= 0) return [];

    let cursor = 0;
    return slices
      .filter((slice) => slice.value > 0)
      .map((slice) => {
        const sweep = (slice.value / total) * 360;
        const start = cursor;
        const end = cursor + sweep;
        cursor = end;
        const mid = start + sweep / 2;
        return { slice, start, end, mid, sweep };
      });
  }, [slices, total]);

  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.22;

  if (total <= 0) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-[var(--color-text-muted)]">
        No sales data
      </div>
    );
  }

  if (variant === "tv") {
    const tvRadius = size * 0.48;
    return (
      <div className={cn("flex h-full w-full items-center justify-center gap-12", className)}>
        <svg
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          className="h-full max-h-[420px] w-auto shrink-0"
          preserveAspectRatio="xMidYMid meet"
        >
          <title>QTD sales share by team</title>
          {arcs.map(({ slice, start, end }) => (
            <path
              key={slice.key}
              d={describePieSlice(cx, cy, tvRadius, start, end - 0.2)}
              fill={slice.color}
              stroke="white"
              strokeWidth={3}
            />
          ))}
        </svg>
        <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-5 gap-y-3 text-2xl">
          {arcs.map(({ slice }) => (
            <Fragment key={slice.key}>
              <span className="h-4 w-4 rounded-full" style={{ backgroundColor: slice.color }} />
              <span className="truncate font-medium text-[var(--color-text)]">{slice.label}</span>
              <span className="text-right font-bold tabular-nums text-[var(--color-text)]">
                {slice.sharePct}%
              </span>
              <span className="text-right tabular-nums text-[var(--color-text-muted)]">
                {formatMetricForCell(slice.value, "currency")}
              </span>
            </Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("mx-auto w-full max-w-[440px]", className)}>
      <svg
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        className="mx-auto h-auto w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <title>QTD sales share by team</title>
        {arcs.map(({ slice, start, end, mid, sweep }) => {
          const labelRadius = radius + (sweep < 12 ? 56 : 40);
          const labelPos = polarToCartesian(cx, cy, labelRadius, mid);
          const elbow = polarToCartesian(cx, cy, radius + 4, mid);
          const anchor = labelPos.x >= cx ? "start" : "end";
          const dx = labelPos.x >= cx ? 6 : -6;

          return (
            <g key={slice.key}>
              <path
                d={describePieSlice(cx, cy, radius, start, end - 0.2)}
                fill={slice.color}
                stroke="white"
                strokeWidth={2}
              />
              <polyline
                points={`${elbow.x},${elbow.y} ${labelPos.x},${labelPos.y} ${labelPos.x + dx},${labelPos.y}`}
                fill="none"
                stroke={slice.color}
                strokeWidth={1.25}
                opacity={0.85}
              />
              <text
                x={labelPos.x + dx}
                y={labelPos.y - 8}
                textAnchor={anchor}
                className="fill-[var(--color-text)] text-[11px] font-semibold"
              >
                {truncateLabel(slice.label)}
              </text>
              <text
                x={labelPos.x + dx}
                y={labelPos.y + 8}
                textAnchor={anchor}
                className="fill-[var(--color-text-muted)] text-[10px]"
              >
                {slice.sharePct}% · {formatMetricForCell(slice.value, "currency")}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
